// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The HUD's two mute controls (M8-09, M8-10).
//
// 06 §4: voice auto-starts on entry "with a mute control in the wrist menu and in the HUD". This
// is the HUD half; `wristMenu.ts` and `wristPanel.ts` are the other, and both read the one
// `VoiceMute` the machine runtime owns, so they cannot disagree. A HUD that said "on" over a wrist
// panel that said "off" would be the worst possible version of this feature.
//
// **Two controls, not one.** "Do not hear me" and "do not hear them" are different needs a player
// has at different moments — see the argument at the top of `voiceMute.ts`. The microphone comes
// first here for the same reason it comes first there: it is the only control in this product
// whose failure is a privacy failure.
//
// **The label states the condition, never the action.** "Mute" on a control that is already muted
// is the standard way a mute button comes to lie: the player reads it, believes it describes what
// is happening, and talks. The accessible name is "Microphone on", "Microphone off" or
// "Microphone blocked"; the `aria-pressed` state says the same thing a second way; and the
// `title` — the hover tooltip, and the one place an *action* belongs — says what a press would do.
//
// **Three states and not two** (M8-10). "Off because you turned it off" and "off because your
// browser refused" are the same sentence to a player and opposite sentences to a control: only one
// of them is undone by pressing it. The blocked case is genuinely `disabled`, which is the one
// place in this shell that is the honest answer — there is nothing to toggle.
//
// **The glyphs are geometry, like every other shipped asset in this product** (content rule: zero
// image files). Not emoji: an emoji is a picture from the operating system's font, which means it
// is a different picture on Windows, Android and the Quest's browser, and on a monochrome fallback
// it is a box. Two paths and a line are the same drawing everywhere.

import { micLabel, voiceLabel, type MicrophoneAccess } from '../../audio/voiceMute'
import { useLocalisation } from '../i18n/useLanguage'

export interface VoiceControlsProps {
  /** This player's microphone is off. */
  readonly micMuted: boolean
  /**
   * What the browser said about a microphone (M8-10).
   *
   * `'refused'` **disables** the microphone control rather than restyling it. That is the one
   * place in this shell where a disabled control is the honest answer: there is genuinely nothing
   * to toggle, and an enabled button that does nothing when it is pressed is worse than no button
   * — a player presses it, sees the label change, and talks. The chip beside it carries the
   * explanation, because a disabled control cannot be relied on to show a tooltip.
   */
  readonly micAccess: MicrophoneAccess
  /** Everybody else's voices are off. */
  readonly voiceMuted: boolean
  readonly onToggleMic: () => void
  readonly onToggleVoice: () => void
}

export function VoiceControls({
  micMuted,
  micAccess,
  voiceMuted,
  onToggleMic,
  onToggleVoice,
}: VoiceControlsProps): React.JSX.Element {
  const { t } = useLocalisation()
  const blocked = micAccess === 'refused'
  return (
    <>
      <button
        type="button"
        className={muteClass(blocked || micMuted)}
        title={
          blocked
            ? t('voice.blocked')
            : micMuted
              ? t('voice.mic-on')
              : t('voice.mic-off')
        }
        // Not `aria-pressed` while blocked: a toggle state on a control that cannot be toggled is
        // a second thing to disbelieve. `aria-disabled` alongside the real `disabled` keeps the
        // control announced rather than skipped, so its name still says what is happening.
        {...(blocked ? { disabled: true, 'aria-disabled': true } : { 'aria-pressed': micMuted })}
        onClick={onToggleMic}
      >
        <MicGlyph muted={blocked || micMuted} />
        <span className="sr-only">{micLabel(micMuted, micAccess)}</span>
      </button>
      <button
        type="button"
        className={muteClass(voiceMuted)}
        title={voiceMuted ? t('voice.chat-on') : t('voice.chat-off')}
        aria-pressed={voiceMuted}
        onClick={onToggleVoice}
      >
        <SpeakerGlyph muted={voiceMuted} />
        <span className="sr-only">{voiceLabel(voiceMuted)}</span>
      </button>
    </>
  )
}

/**
 * Not-being-heard is a modifier class rather than an inline colour.
 *
 * The stylesheet decides what "off" looks like in each theme, and there are two of them. It is
 * also the third cue after the label and `aria-pressed`, which is the same rule the navigation
 * icons follow: a colour is never the only thing carrying a state. A blocked microphone wears the
 * same class as a muted one on purpose — at a glance the two *are* the same fact, and the label,
 * the disabled state and the chip are what separate them.
 */
function muteClass(muted: boolean): string {
  return muted ? 'button button--quiet stage__mute stage__mute--off' : 'button button--quiet stage__mute'
}

/**
 * A microphone, struck through when it is off.
 *
 * `aria-hidden`, always: the button carries its own name from the `sr-only` label beside it, and a
 * screen reader announcing the drawing as well would read every control twice.
 */
function MicGlyph({ muted }: { readonly muted: boolean }): React.JSX.Element {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x={9} y={3} width={6} height={10} rx={3} />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
      {muted ? <path d="M4 4l16 16" /> : null}
    </svg>
  )
}

/** A speaker, struck through when voice chat is off. */
function SpeakerGlyph({ muted }: { readonly muted: boolean }): React.JSX.Element {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      {muted ? <path d="M17 8l5 8M22 8l-5 8" /> : <path d="M16.5 8.5a5 5 0 0 1 0 7" />}
    </svg>
  )
}
